<!-- Dialog to select one or more Facebook pages to share a video to.

Can specify an optional description to go with the post. User must
confirm that he is allowed to post the video.

-->

<template>
  <v-dialog
    transition="dialog-bottom-transition"
    persistent
    lazy
    scrollable
    :value="show"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="$store.state.common.largeDialogWidth"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title class="pb-0 mb-3">
        <v-layout align-center>
          <div class="headline">
            <v-layout align-center>
              <v-img
                src="/static/icons8-facebook-96.png"
                width="36px"
                class="mr-2"
              />
              {{ dialogTitle }}
            </v-layout>
          </div>
          <v-btn
            class="ml-auto mr-0 my-0"
            :disabled="loading"
            flat
            icon
            :title="$t('abort')"
            @click="onAborted()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-progress-linear
        v-if="progress !== null"
        v-model="progress"
        color="msr"
        size="4"
        class="mt-0 mb-0"
      />
      <!-- Preview -->
      <v-layout
        v-if="tvspot.editable_template"
        column
        style="border-bottom: 1px solid rgba(0, 0, 0, 0.16)"
      >
        <!-- Branding Dropdown -->
        <v-layout v-if="showBrandingsDropdown">
          <tv-template-branding-widget
            ref="brandingwidget"
            class="ml-auto"
            :title="lastBrandingApplied ? lastBrandingApplied.name : null"
            :overlay-options="tvspot.overlay_options"
            :brandings="brandings"
            :active="lastBrandingApplied !== null"
            @applied="onBrandingApplied"
          />
        </v-layout>
        <tv-template-preview
          ref="preview"
          class="mx-auto"
          :html-overlay="tvspot.editable_template.html_overlay"
          :background-image="tvspot.editable_template.video_poster_for_editing"
          :tvtemplate="tvspot.editable_template"
          :width="tvspot.tvformat.width"
          :height="tvspot.tvformat.height"
          :max-width="$store.state.common.largeDialogWidth"
          :is-multiscreen="tvspot.is_multiscreen"
          :onload-callback="populateIframeWithInitialOverlayData"
          show-zoom-buttons
        />
      </v-layout>
      <v-img
        v-else
        :src="posterURL"
        max-height="360px"
        class="mb-3"
        contain
      />
      <v-card-text class="pt-0">
        <!-- OverlayData for editable HTML-Spot -->
        <h3
          v-if="tvspot.editable_template"
          class="mt-3 mb-2 grey--text text--darken-2"
        >
          {{ $t('shareOnFacebookOverlayData') }}:
        </h3>
        <overlay-data
          v-if="tvspot.editable_template"
          ref="overlaydata"
          :key="show"
          class="grow-1"
          validate-on-start
          hide-flagged-groups-during-activation
          :overlay-options="tvspot.overlay_options"
          @changed="refreshIframeWithOverlayData()"
          @valid="onOverlayDataValidityChanged"
        />
        <!-- Facebook Options -->
        <h3
          v-if="tvspot.editable_template"
          class="my-2 grey--text text--darken-2"
        >
          {{ $t('shareOnFacebookOptions') }}
        </h3>
        <v-select
          v-model="chosenFacebookPages"
          :items="pagesAndLocations"
          :label="$t('shareOnTheseFacebookPages')"
          :disabled="loading"
          :menu-props="{contentClass: 'facebook-pages-menu'}"
          class="mb-4"
          item-text="name"
          multiple
          hide-details
          return-object
        >
          <template
            slot="item"
            slot-scope="data"
          >
            <v-list-tile-action style="min-width: 36px">
              <v-checkbox
                color="msr"
                hide-details
                :input-value="isSelectedPageId(data.item.id)"
                style="pointer-events: none"
              />
            </v-list-tile-action>
            <v-list-tile-action class="mr-3">
              <v-img
                v-if="data.item.photos"
                :src="
                  data.item.photos.data[0].images[
                    data.item.photos.data[0].images.length - 1
                  ].source
                "
                class="ma-1"
              />
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ data.item.name }}
              </v-list-tile-title>
              <v-list-tile-sub-title v-if="data.item.isLocationPage">
                <span
                  v-if="$vuetify.breakpoint.xs"
                >{{ data.item.city }},&nbsp;</span>
                <span>{{ data.item.address }}</span>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </template>
        </v-select>
        <v-textarea
          v-model="description"
          rows="2"
          auto-grow
          :label="$t('shareVideoOnFacebookDescriptionLabel')"
          :placeholder="$t('shareVideoOnFacebookDescriptionPlaceholder')"
          :disabled="loading"
          hide-details
        />
        <!-- Optional scheduled publish -->
        <v-layout
          align-baseline
          :class="{
            'mt-1': !scheduled,
            'mt-3': scheduled && $vuetify.breakpoint.smAndUp,
          }"
          wrap
        >
          <v-select
            v-model="scheduled"
            hide-details
            class="grow-0"
            style="min-width: 230px"
            :disabled="loading"
            :items="scheduleOptions"
          />
          <v-slide-x-transition>
            <v-layout
              v-if="scheduled"
              wrap
              :class="{
                'mt-3': $vuetify.breakpoint.xs,
                'ml-4': $vuetify.breakpoint.smAndUp,
              }"
            >
              <!-- Schedule Date -->
              <v-menu
                :min-width="mobileDatepickerMinWidth"
                lazy
              >
                <v-text-field
                  slot="activator"
                  readonly
                  hide-details
                  :label="$t('shareContentPlannedDateLabel')"
                  :title="$t('chooseDate')"
                  :value="scheduledDate"
                  style="max-width: 200px"
                  :disabled="loading"
                />
                <v-date-picker
                  v-model="scheduledDate"
                  color="msr"
                  scrollable
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :locale="$t('_datepickerLocale')"
                  :allowed-dates="isAllowedScheduledDate"
                  :disabled="loading"
                />
              </v-menu>
              <!-- Schedule Time -->
              <time-picker
                :time.sync="scheduledTime"
                :title="$t('chooseTime')"
                :minute-step-size="1"
                :disabled="loading"
                class="ml-4"
              />
            </v-layout>
          </v-slide-x-transition>
        </v-layout>
        <!-- Confirm legal -->
        <v-checkbox
          v-model="legalInformationConfirmed"
          :label="$t('shareVideoOnFacebookConfirmHint')"
          :disabled="loading"
          color="msr"
          hide-details
          class="mt-4"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <btn-ok
          icon="share"
          round
          :loading="loading"
          :disabled="!formIsValid"
          :title="$t('shareContentOnSocialMedia')"
          @click="onConfirmed()"
        />
        <v-spacer />
      </v-card-actions>
    </v-card>
    <!-- Template Image Replacement -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onImageChangeConfirmed"
    />
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs';
import orderBy from 'lodash.orderby';

import {
  chooseDefaultBrandingFromBrandings,
  getFallbackBrandingFromTVSpotTemplateData,
} from '@/mixins/tvtemplateBrandings';
import {getDefaultOverlayDataForOverlayOptions} from '@/mixins/overlayOptions.js';
import {onEditableTVSpotTemplateImageReplacementCropped} from '@/mixins/imageReplacement';
import mobileDatepickerMinWidth from '@/mixins/mobileDatepickerMinWidth.js';

import BtnOk from '@/components/ButtonOk';
import ImageReplacement from '@/components/ImageReplacement';
import OverlayData from '@/components/OverlayData';
import TimePicker from '@/components/TimePicker';
import TvTemplateBrandingWidget from '@/components/TvTemplateBrandingWidget.vue';
import TvTemplatePreview from '@/components/TvTemplatePreview';

const defaults = {
  legalInformationConfirmed: false,
  description: '',
  chosenFacebookPages: [],
};

export default {
  components: {
    BtnOk,
    ImageReplacement,
    OverlayData,
    TimePicker,
    TvTemplateBrandingWidget,
    TvTemplatePreview,
  },
  mixins: [mobileDatepickerMinWidth],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** Optional sharing progress value between 0-100. */
    progress: {
      type: Number,
      default: null,
    },
    /** A TVSpot. */
    tvspot: {
      type: Object,
      required: true,
    },
    /** Facebook pages as received via Graph API permission 'pages_show_list'. */
    pages: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      legalInformationConfirmed: defaults.legalInformationConfirmed,
      description: defaults.description,
      chosenFacebookPages: defaults.chosenFacebookPages,
      isOverlayDataValid: true,

      scheduleOptions: [
        {text: this.$t('shareContentImmediately'), value: false},
        {text: this.$t('shareContentPlanned'), value: true},
      ],
      scheduled: false,
      scheduledDate: null,
      scheduledTime: '08:00',
    };
  },
  computed: {
    brandings() {
      return this.$store.state.common.brandings;
    },
    defaultBranding() {
      return chooseDefaultBrandingFromBrandings(this.brandings);
    },
    fallbackBranding() {
      return getFallbackBrandingFromTVSpotTemplateData(this.tvspot);
    },
    initialBranding() {
      return this.defaultBranding || this.fallbackBranding;
    },
    lastBrandingApplied: {
      get() {
        return this.$store.state.common.lastBrandingApplied;
      },
      set(branding) {
        this.$store.commit('SET_LAST_BRANDING_APPLIED', branding);
      },
    },
    showBrandingsDropdown() {
      return (
        this.$store.getters.userHasContentCreatorBooking &&
        !!this.tvspot.editable_template &&
        this.tvspot.editable_template.is_brandable
      );
    },
    dialogTitle() {
      const num =
        this.chosenFacebookPages.length <= 1
          ? ''
          : this.chosenFacebookPages.length;
      return this.$t('shareVideoOnNumFacebookPages').replace('{{ num }}', num);
    },
    /**
     * Unpack locations to be also listed as selectable pages.
     *
     * Handle duplicates e.g. a page is listed normally and then again
     * as a location of some other page. The first instance will then be
     * removed so only the location version of it will be displayed.
     * Otherwise a main page may be hidden unintentionally as the
     * dropdown will only show the first occurence of items with the
     * same name.
     */
    pagesAndLocations() {
      let allPages = [];
      const initalOrderedPages = orderBy(this.pages, 'name');

      for (let page of initalOrderedPages) {
        allPages.push(page);
        if (!page.locations) {
          continue;
        }

        // Create additional page objects from location pages. These
        // will be marked as 'isLocationPage'. We'll display them a bit
        // differently in the dropdown (appending the city name) and
        // make sure they are not included in the main list more than
        // once.
        let childPages = [];
        for (let locationPage of page.locations.data) {
          const childPage = {
            // General page fields
            access_token: locationPage.access_token,
            id: locationPage.id,
            link: locationPage.link,
            name: page.name + ` (${locationPage.location.city})`,
            photos: locationPage.photos,
            instagram_business_account: locationPage.instagram_business_account,

            // Location-page specific fields
            address: `${locationPage.location.zip}, ${locationPage.location.street}`,
            city: locationPage.location.city,
            isLocationPage: true,
          };
          childPages.push(childPage);
        }

        // Drop pages from the main list if they will also be added as a
        // location page (we keep the location page only). Otherwise the
        // dropdown will implicitly show the first occurrence only as
        // they share the same name/key.
        const childPageIds = childPages.map((page) => page.id);
        let i = allPages.length;
        while (i--) {
          const page = allPages[i];
          if (childPageIds.indexOf(page.id) !== -1) {
            console.log(
              '[DEBUG] Deduplicating page that is also included as a location page:',
              page
            );
            allPages.splice(i, 1);
          }
        }

        // Add sorted blocks of child pages, so overall list stays sorted.
        childPages = orderBy(childPages, 'city');
        allPages = allPages.concat(childPages);
      }

      return allPages;
    },
    posterURL() {
      return this.tvspot.poster_url;
    },
    formIsValid() {
      const scheduleValid =
        !this.scheduled || (!!this.scheduledDate && !!this.scheduledTime);
      return (
        this.isOverlayDataValid &&
        this.legalInformationConfirmed &&
        this.chosenFacebookPages.length > 0 &&
        scheduleValid
      );
    },
  },
  watch: {
    /** Reset when dialog is toggled. */
    show: function (show) {
      if (!show) {
        this.reset();
      } else {
        this.maybePreselectChosenFacebookPages();
      }
    },
    /** Preselect page if pages list comes in late. */
    pages: {
      immediate: true,
      handler: function () {
        if (!this.show) {
          return;
        }
        this.maybePreselectChosenFacebookPages();
      },
    },
  },
  created() {
    this.$store.dispatch('MAYBE_LIST_BRANDINGS').then(this.initializeBranding);
  },
  methods: {
    isAllowedScheduledDate(scheduledDateString) {
      const scheduledDate = dayjs(scheduledDateString);
      const today = dayjs(new Date());
      return (
        scheduledDate.isSame(today, 'day') ||
        scheduledDate.isAfter(today, 'day')
      );
    },
    maybePreselectChosenFacebookPages() {
      if (!this.pages || !this.pages.length) {
        return;
      }
      const onlyOnePage = this.pages.length === 1;
      const firstPage = this.pages[0];
      const firstPageHasNoLocations =
        !firstPage.locations || !firstPage.locations.length;
      const noPagesSelectedYet = !this.chosenFacebookPages.length;

      if (onlyOnePage && firstPageHasNoLocations && noPagesSelectedYet) {
        this.chosenFacebookPages = [firstPage];
      }
    },
    isSelectedPageId(pageId) {
      return this.chosenFacebookPages.map((page) => page.id).includes(pageId);
    },
    initializeBranding() {
      if (!this.initialBranding) {
        return;
      }
      const matchingBranding =
        this.brandings.filter(
          (branding) => branding.id == this.defaultBranding.id
        )[0] || this.fallbackBranding;
      if (matchingBranding) {
        this.lastBrandingApplied = matchingBranding;
      }
    },
    onBrandingApplied(context) {
      this.lastBrandingApplied = context.branding;
      this.refreshIframeWithOverlayData();
    },
    injectLastBrandingAppliedIntoOverlayData(overlayData) {
      if (!overlayData.branding) {
        overlayData.branding = {};
      }
      if (this.lastBrandingApplied) {
        overlayData.branding = this.lastBrandingApplied;
      }
    },
    injectTvSpotDurationIntoOverlayData(overlayData) {
      overlayData.duration = this.tvspot.duration;
    },
    populateIframeWithInitialOverlayData() {
      const overlayData = getDefaultOverlayDataForOverlayOptions(
        this.tvspot.overlay_options
      );
      this.injectTvSpotDurationIntoOverlayData(overlayData);
      this.injectLastBrandingAppliedIntoOverlayData(overlayData);

      const previewWidget = this.$refs.preview;
      previewWidget.updateOverlayData(overlayData);
    },
    refreshIframeWithOverlayData() {
      const overlayDataWidget = this.$refs.overlaydata;
      if (!overlayDataWidget) {
        return;
      }

      const overlayData = overlayDataWidget.getOverlayData();
      this.injectTvSpotDurationIntoOverlayData(overlayData);
      this.injectLastBrandingAppliedIntoOverlayData(overlayData);

      const previewWidget = this.$refs.preview;
      previewWidget.updateOverlayData(overlayData);
    },
    /**
     * Update form validation flag when form widget reports changes.
     */
    onOverlayDataValidityChanged(allFieldsAreValid) {
      this.isOverlayDataValid = allFieldsAreValid;
    },
    onImageChangeConfirmed(payload) {
      onEditableTVSpotTemplateImageReplacementCropped(
        this.tvspot.editable_template,
        this.tvspot.overlay_options,
        payload.overlayDataFieldName,
        payload.file,
        payload.originalFilename
      );
    },
    reset() {
      this.chosenFacebookPages = defaults.chosenFacebookPages;
      this.description = defaults.description;
      this.legalInformationConfirmed = defaults.legalInformationConfirmed;
      this.isOverlayDataValid = true;
      this.$emit('update:progress', null);
    },
    getScheduledPublishTime() {
      const timeParts = this.scheduledTime.split(':');
      const hour = Number(timeParts[0]);
      const minute = Number(timeParts[1]);

      let combined = dayjs(this.scheduledDate);
      combined = combined.hour(hour);
      combined = combined.minute(minute);

      // https://day.js.org/docs/en/display/unix-timestamp
      return combined.unix();
    },
    onAborted() {
      this.reset();
      this.$emit('update:show', false);
      this.$emit('aborted');
    },
    onConfirmed() {
      let overlayData = null;
      if (this.tvspot.editable_template) {
        const overlayDataWidget = this.$refs.overlaydata;
        if (overlayDataWidget) {
          overlayData = overlayDataWidget.getOverlayData();
          this.injectLastBrandingAppliedIntoOverlayData(overlayData);
          this.injectTvSpotDurationIntoOverlayData(overlayData);
        }
      }

      const chosenPagesPayload = [];
      for (let page of this.chosenFacebookPages) {
        const pagePayload = {
          description: this.description,
          pageAccessToken: page.access_token,
          pageId: page.id,
          pageLink: page.link,
          pageName: page.name,
          tvspotId: this.tvspot.id,
        };
        if (overlayData) {
          pagePayload.overlayData = overlayData;
        }
        if (this.scheduled) {
          pagePayload.scheduledPublishTime = this.getScheduledPublishTime();
        }

        chosenPagesPayload.push(pagePayload);
      }
      this.$emit('confirmed', chosenPagesPayload);
    },
  },
};
</script>

<style>
.facebook-pages-menu .v-list__tile {
  height: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/components/ShareOnFacebookPageDialog.vue